import { Input, Steps, Typography, Button as AntdButton, Tooltip, Form } from 'antd'
import Modal from 'components/Modal/Modal'
import { Button as RoundedButton } from "components/Buttons/Buttons";
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2';
import OTPInput from 'components/OTP';
import { useDispatch, useSelector } from 'react-redux';
import { resendEmailConfirmationToken, resendPhoneConfirmationToken, sendEmailConfirmationToken, sendPhoneNumberConfirmationToken, verifyEmailConfirmationToken, verifyPhoneConfirmationToken, verifyUser } from 'services/auth.services';
import { openNotificationWithIcon } from "utils/Notification";
const { Step } = Steps;

const { Title, Text } = Typography;

export const TFA_METHODS = {
    email: 'email',
    sms: 'sms'
}

// helper to format seconds -> M:SS
const fmt = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

const EmailTFASetup = ({
    settingsLoading, tfaExpiry, onClose
}) => {
    const dispatch = useDispatch();
    const [step, setStep] = React.useState(0);
    // total seconds derived from minutes prop (ensure at least 60s)
    const totalSeconds = Math.max(60, Math.floor((Number(tfaExpiry) || 1) * 60));
    const [timer, setTimer] = React.useState(totalSeconds);
    const intervalRef = useRef(null);

    const [email, setEmail] = React.useState("");
    const [emailError, setEmailError] = React.useState(false);

    const user = useSelector(state => state.user.currentUser);
    const { phoneNumberConfirmed, emailConfirmed, preferredTwoFactorVerificationMode } = user

    const [otp, setOtp] = useState("")


    useEffect(() => {
        setEmail(user.email ?? "")
    }, [user])


    // start/stop timer when in verification step
    useEffect(() => {
        // reset timer when entering step 1
        if (step === 1) {
            setTimer(totalSeconds);
        } else {
            // leave timer as-is; clear interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [step, totalSeconds]);

    useEffect(() => {
        if (step !== 1) return;
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timer <= 0) return;

        intervalRef.current = setInterval(() => {
            setTimer(t => {
                if (t <= 1) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    return 0;
                }
                return t - 1;
            })
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [timer, step]);

    // allow resend only after 1 minute has elapsed from start OR when remaining <= totalSeconds - 60
    const canResend = () => {
        const elapsed = totalSeconds - timer;
        return elapsed >= 60 || timer === 0;
    }

    const resendTooltip = () => {
        const total = Math.max(0, Math.floor((tfaExpiry || 0) * 60))
        if (!total) return ''
        const elapsed = total - (timer ?? 0)
        const remaining = Math.max(0, 60 - elapsed)
        return remaining > 0 ? `New code can only be requested after ${remaining} seconds` : ''
    }

    const handleSubmit = useCallback(async () => {
        switch (step) {
            case 0:
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    setEmailError(true);
                    return;
                } else {
                    setEmailError(false);
                }

                // if email confirmed and input is equal to email then show toast that it is already confirmed
                if (email === user?.email && emailConfirmed) {
                    openNotificationWithIcon('error', 'Email Already Confirmed',
                        'Two-factor Authentication is already setup on this email address'
                    )
                    return
                }

                // call the api to send the code to the email
                // if api is successful then proceed to the next step
                const res = await dispatch(sendEmailConfirmationToken(user?.staffId, email))
                if (res?.status === 'success')
                    setStep(1);
                break;
            case 1:
                // verify the code
                const res2 = await dispatch(verifyEmailConfirmationToken(otp, email))
                if (res2?.status === 'success') {
                    // handle onclose
                    // dispatch(verifyUser())
                    onClose()
                }
                break;
            default:
                break;
        }
    }, [step, email, otp, user])

    return <Form onFinish={handleSubmit}>
        <Steps current={step} >
            <Step title="Step 1" />
            <Step title="Step 2" />
        </Steps>
        <div style={{ marginBottom: 16 }}>

            {
                step === 0 && <>
                    <Title level={4} style={{ marginTop: 16, marginBottom: 0, fontSize: 16 }}>Enter the email address</Title>
                    <Text style={{ color: '#666', fontSize: 14, }}>We will send a verification code to this email address</Text>
                </>
            }
            {
                step === 1 && <>
                    <Title level={4} style={{ marginTop: 16, marginBottom: 0, fontSize: 16 }}>Verify your email</Title>
                    <Text style={{ color: '#666', fontSize: 14, }}>Enter the verification code sent to your email</Text>
                    <Text style={{ color: '#666', fontSize: 14, display: 'block' }}>{email}</Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        This code will expire in {fmt(timer)}
                    </Text>
                </>
            }
        </div>

        <div style={{ marginBottom: 24 }}>
            {
                step === 0 && <>
                    <Input placeholder='Email address' type="email" style={{
                        paddingTop: 5, paddingBottom: 6, borderRadius: 4,
                        borderColor: emailError ? 'red' : '#CACACA',
                    }}
                        value={email} onChange={e => {
                            setEmail(e.target.value);
                            setEmailError(false);
                        }}
                    />
                    {emailError && <Text style={{ color: 'red', fontSize: 13 }}>Please enter a valid email address</Text>}
                </>
            }
            {
                step === 1 &&
                <>
                    <OTPInput length={6} value={otp} onChange={v => setOtp(v)} autoFocus />
                    <div style={{ marginTop: 16 }}>
                        <Text style={{ color: '#666', fontSize: 14, }}>
                            Didn't receive the code?
                            <Tooltip title={resendTooltip()}>
                                <AntdButton type='text' onClick={() => {
                                    if (!canResend()) return;
                                    // resend the code -> restart timer
                                    // call the api

                                    dispatch(resendEmailConfirmationToken(user?.staffId, email)).then(() => {
                                        setTimer(totalSeconds)
                                    }).catch(() => { })
                                }}
                                    disabled={!canResend()}
                                    style={{ margin: 0, padding: 0 }}
                                >Resend</AntdButton>
                            </Tooltip>
                        </Text>
                    </div>
                </>
            }
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {step === 0 && <div></div>}
            {
                step === 1 && <AntdButton
                    type="text" style={{ padding: 0 }} onClick={() => {
                        setStep(0)
                        setTimer(totalSeconds)
                        setOtp("")
                    }}>
                    Change Email
                </AntdButton>
            }
            <RoundedButton
                rounded
                onClick={() => {
                    handleSubmit()
                }}
                loading={settingsLoading}
            >
                Next
            </RoundedButton>
        </div>
        <button type='submit' hidden>hidden</button>
    </Form>
}


const SMSTFASetup = ({
    settingsLoading, tfaExpiry, onClose
}) => {
    const [step, setStep] = React.useState(0);
    const totalSeconds = Math.max(60, Math.floor((Number(tfaExpiry) || 1) * 60));
    const [timer, setTimer] = React.useState(totalSeconds);
    const intervalRef = useRef(null);

    const [number, setNumber] = React.useState("");
    const [formattedNumber, setFormattedNumber] = React.useState("");
    const [phoneError, setPhoneError] = React.useState(false);
    const user = useSelector(state => state.user.currentUser);
    const { phoneNumberConfirmed, emailConfirmed, preferredTwoFactorVerificationMode } = user

    const [otp, setOtp] = useState("")

    const dispatch = useDispatch()

    useEffect(() => {
        let phoneNumber = user?.phoneNumber || "";
        setNumber(phoneNumber || "");
        let digits = phoneNumber.replace(/\D/g, "");
        if (digits.length === 11 && digits.startsWith("1")) {
            digits = digits.slice(1);
        }
        setFormattedNumber(`+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}` || "");
    }, [user])


    useEffect(() => {
        if (step === 1) {
            setTimer(totalSeconds);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [step, totalSeconds]);

    useEffect(() => {
        if (step !== 1) return;
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timer <= 0) return;

        intervalRef.current = setInterval(() => {
            setTimer(t => {
                if (t <= 1) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    return 0;
                }
                return t - 1;
            })
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [timer, step]);

    const canResend = () => {
        const elapsed = totalSeconds - timer;
        return elapsed >= 60 || timer === 0;
    }

    const resendTooltip = () => {
        const total = Math.max(0, Math.floor((tfaExpiry || 0) * 60))
        if (!total) return ''
        const elapsed = total - (timer ?? 0)
        const remaining = Math.max(0, 60 - elapsed)
        return remaining > 0 ? `New code can only be requested after ${remaining} seconds` : ''
    }


    const handleSubmit = useCallback(async () => {
        switch (step) {
            case 0:
                if (number.length < 10) {
                    setPhoneError(true);
                    return;
                } else {
                    setPhoneError(false);
                }


                // if email confirmed and input is equal to email then show toast that it is already confirmed
                if (number === user?.phoneNumber && phoneNumberConfirmed) {
                    openNotificationWithIcon('error', 'Email Already Confirmed',
                        'Two-factor Authentication is already setup on this phone number'
                    )
                    return
                }
                // call the api to send the code to the phone number
                // if api is successful then proceed to the next step
                const res = await dispatch(sendPhoneNumberConfirmationToken(user?.staffId, number))
                if (res?.status === 'success') {
                    setStep(1);
                }
                break;
            case 1:
                console.log('otp', otp)
                // verify the code
                const res2 = await dispatch(verifyPhoneConfirmationToken(otp, number))
                if (res2?.status === 'success') {
                    dispatch(verifyUser())
                    onClose()
                }
                break;
            default:
                break;
        }
    }, [step, number, otp, user])

    return <Form onFinish={() => {
        console.log('first',)
        handleSubmit()
    }}
        onFinishFailed={(e) => {
            console.log('error', e)
        }}
    >
        <Steps current={step} >
            <Step title="Step 1" />
            <Step title="Step 2" />
        </Steps>

        <div style={{ marginBottom: 16 }}>
            <Title level={4} style={{ marginTop: 16, marginBottom: 0, fontSize: 16 }}>Enter the phone number</Title>
            <Text style={{ color: '#666', fontSize: 14, }}>
                {step === 0 && "We will send a verification code to this phone number"}
                {step === 1 && `Kindly provide the verification code sent to your phone number `}
            </Text>

            {step === 1 && <Text style={{ color: '#666', fontSize: 14, display: 'block', }}>{`${formattedNumber}`}</Text>}

        </div>

        <div style={{ marginBottom: 24 }}>
            {
                step === 0 && <>
                    <PhoneInput country={"us"}
                        onlyCountries={["us"]}
                        value={number}
                        onChange={(phone, x, y, formattedValue) => { setNumber(phone); setFormattedNumber(formattedValue); setPhoneError(false); }}
                        inputStyle={{
                            borderColor: phoneError ? 'red' : '#CACACA',
                        }}
                        buttonStyle={{
                            borderColor: phoneError ? 'red' : '#CACACA',
                        }}
                        onEnterKeyPress={handleSubmit}
                    />
                </>
            }
            {phoneError && <Text style={{ color: 'red', fontSize: 13 }}>Please enter a valid phone number</Text>}
            {
                step === 1 &&
                <>
                    <OTPInput length={6} value={otp} onChange={v => setOtp(v)} autoFocus />
                    <div style={{ marginTop: 16 }}>
                        <Text style={{ color: '#666', fontSize: 14, }}>
                            Didn't receive the code?
                            <Tooltip title={resendTooltip()}>
                                <AntdButton type='text' onClick={() => {
                                    if (!canResend()) return;
                                    dispatch(resendPhoneConfirmationToken(user?.staffId, number)).then(() => {
                                        setTimer(totalSeconds)
                                    }).catch(() => { })
                                }}
                                    disabled={!canResend()}
                                    style={{ margin: 0, padding: 0 }}
                                >Resend</AntdButton></Tooltip>
                        </Text>
                    </div>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        This code will expire in {fmt(timer)}
                    </Text>
                </>
            }

        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {step === 0 && <div></div>}
            {
                step === 1 && <AntdButton
                    type="text" style={{ padding: 0 }} onClick={() => {
                        setStep(0)
                        setTimer(totalSeconds)
                        setOtp("")
                    }}>
                    Change Number
                </AntdButton>
            }
            <RoundedButton
                rounded
                onClick={() => {
                    handleSubmit()
                }}
                loading={settingsLoading}
            >
                Next
            </RoundedButton>
        </div>
        <button type='submit' hidden>Submit</button>
    </Form>
}



export default function TwoFactorAuthModal({
    visible, type, onClose, onLoad, settingsLoading, tfaExpiry
}) {

    useEffect(() => {
        if (visible) {
            onLoad()
        }
    }, [visible])

    return (
        <Modal open={visible} title="Two-Factor Authentication" footer={null} onCancel={onClose}
            destroyOnClose={true}
        >
            {type === TFA_METHODS.email && <EmailTFASetup settingsLoading={settingsLoading} tfaExpiry={tfaExpiry} onClose={onClose} />}
            {type === TFA_METHODS.sms && <SMSTFASetup settingsLoading={settingsLoading} tfaExpiry={tfaExpiry} onClose={onClose} />}
        </Modal>
    )
}